import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Subject, takeUntil } from 'rxjs';
import { PanoramerStateService, PanoramerQueueItem } from '../../_services/panoramer-state.service';
import { IpfHubService } from '../../_services/ipf-hub.service';
import { PanoramerViewComponent } from '../panoramer-view/panoramer-view.component';
import { AzureBlobService } from '../../shared/_services/AzureBlobService';
import { environment } from '../../../environments/environment';
import { BATCH_SIZE_OF_FILE, BLOB_CONCURRENCY_UPLOAD } from 'src/app/_helpers/Constants';
import { ProcessFilesService } from '../../_services/video-image-generation.service';
import { DamageImageFrame } from '../../_models/video-image-generation.model';
import { DamageImageGenerationStatus } from '../../upload/models/FileOfUpload';

interface PendingBatchEntry {
  uploadId: string;
  fileId: string;
  uploadedPathId: string;
  frames: DamageImageFrame[];
}

@Component({
  selector: 'app-panoramer-global',
  templateUrl: './panoramer-global.component.html',
  styleUrls: ['./panoramer-global.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [PanoramerViewComponent],
})
export class PanoramerGlobalComponent implements OnInit, OnDestroy {
  activeItem: PanoramerQueueItem | null = null;
  selectedDamage: PanoramerQueueItem | undefined = undefined;
  /** Toggled false→true between items to force viewer destroy/recreate. */
  viewerVisible = false;
  processedCount = 0;
  remainingCount = 0;

  /** Frames accumulated per fileUrl; flushed when the URL changes or queue empties. */
  private readonly pendingFrames = new Map<string, PendingBatchEntry>();

  private readonly destroy$ = new Subject<void>();

  /** Track the upload ID for the current processing session */
  private currentUploadId: string | null = null;

  /** Item whose upload was stopped while its screenshot was still uploading — its result is dropped. */
  private abortedItem: PanoramerQueueItem | null = null;

  constructor(
    private readonly panoramerState: PanoramerStateService,
    private readonly sanitizer: DomSanitizer,
    private readonly cdr: ChangeDetectorRef,
    private readonly azureBlobService: AzureBlobService,
    private readonly ProcessFilesService: ProcessFilesService,
    private readonly ipfHubService: IpfHubService
  ) {}

  ngOnInit(): void {
    this.panoramerState.queueLength$
      .pipe(takeUntil(this.destroy$))
      .subscribe((len) => {
        this.remainingCount = len;
        this.cdr.markForCheck();
      });

    this.panoramerState.currentItem$
      .pipe(takeUntil(this.destroy$))
      .subscribe((item) => {

        const prevFileUrl = this.activeItem?.fileUrl ?? null;

        // Hide viewer so Angular destroys the old PanoramoViewerNG instance.
        this.viewerVisible = false;
        this.activeItem = item;

        // fileUrl changed → flush batch for the previous file
        if (prevFileUrl && prevFileUrl !== item?.fileUrl) {
          this.flushBatch(prevFileUrl);
        }

        if (item) {
          // Track upload ID from the first item in the queue
          if (!this.currentUploadId && item.uploadId) {
            this.currentUploadId = item.uploadId;
          }

          // Set InProgress via hub BEFORE showing the viewer (await ensures DB + broadcast
          // complete before processing starts, so other clients see InProgress).
          const showViewer = () => {
            setTimeout(() => {
              this.viewerVisible = true;
              this.cdr.markForCheck();
            }, 0);
          };

          if (item.fileId && item.uploadId && item.fileUrl !== prevFileUrl) {
            this.ipfHubService.updateFileStatus(
              item.uploadId,
              item.fileId,
              DamageImageGenerationStatus.InProgress
            ).then(() => showViewer())
             .catch(err => {
               console.error('Failed to set InProgress via hub:', err);
               showViewer();
             });
          } else {
            showViewer();
          }
        } else {
          // Queue finished → flush everything that's still pending
          this.flushAllBatches();
          this.processedCount = 0;
          this.currentUploadId = null;
        }

        this.cdr.markForCheck();
      });

    // Generation stopped from another tab/admin — abort this upload's work here too
    this.ipfHubService.generationStopped$
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ uploadId }) => this.panoramerState.removeUpload(uploadId));

    // Fires before currentItem$ moves on, so the stopped upload's partial batch is gone before
    // flushBatch could report it as Success over the InterruptedByUser written by Stop
    this.panoramerState.uploadRemoved$
      .pipe(takeUntil(this.destroy$))
      .subscribe((uploadId) => {
        const id = uploadId.toLowerCase();
        if (this.activeItem?.uploadId.toLowerCase() === id) {
          this.abortedItem = this.activeItem;
        }
        this.discardPendingFrames(id);
      });
  }

  ngOnDestroy(): void {
    this.flushAllBatches();
    this.destroy$.next();
    this.destroy$.complete();
  }

  getSafeUrl(url: string): SafeResourceUrl {
    return this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }

  move(): void {
    this.selectedDamage = this.activeItem!;
  }

  async onAutoScreenshot(dataUrl: string): Promise<void> {
    if (this.isFromReplacedViewer()) return;
    if (!this.activeItem?.uploadedPathId || !this.activeItem?.damageId) {
      this.skipCurrent();
      return;
    }
    // Snapshot current item NOW — skipCurrent() mutates activeItem before any
    // awaits below resolve, so we'd lose the IDs otherwise.
    const item = this.activeItem;

    try {
      const blob = await (await fetch(dataUrl)).blob();

      const blobPath = `${item.uploadedPathId}/screenshots/${item.damageId}.png`;
      const containerClient = this.azureBlobService.initBlob(environment.container360Image);
      const blockBlobClient = containerClient.getBlockBlobClient(blobPath);

      await blockBlobClient.uploadData(blob, {
        blockSize: BATCH_SIZE_OF_FILE,
        concurrency: BLOB_CONCURRENCY_UPLOAD,
        blobHTTPHeaders: { blobContentType: 'image/png' },
      });

      const screenshotUrl = blockBlobClient.url.split('?')[0];

      if (this.dropIfAborted(item)) return;
      this.addFrame(item, {
        objectType: item.objectType,
        damageId: item.damageId,
        status: DamageImageGenerationStatus.Success,
        imageUrl: screenshotUrl,
      });

      this.completeIfCurrent(item);
    } catch (error) {
      console.error('Panoramo screenshot upload failed', error);
      if (this.dropIfAborted(item)) return;
      this.addFrame(item, {
        objectType: item.objectType,
        damageId: item.damageId,
        status: DamageImageGenerationStatus.BlobUploadFailed,
        imageUrl: '',
      });
      this.completeIfCurrent(item);
    }
  }

  /** Called when the viewer emits onLoadError (file not found, init failure, screenshot error). */
  onViewerError(): void {
    if (this.isFromReplacedViewer()) return;
    if (this.activeItem) {
      const item = this.activeItem; // snapshot before skipCurrent() mutates activeItem
      this.addFrame(item, {
        objectType: item.objectType,
        damageId: item.damageId,
        status: DamageImageGenerationStatus.SourceFileCorrupted,
        imageUrl: '',
      });
    }
    this.skipCurrent();
  }

  skipCurrent(): void {
    this.processedCount++;
    this.panoramerState.completeCurrentItem();
  }

  cancelAll(): void {
    this.flushAllBatches();
    this.processedCount = 0;
    this.panoramerState.clearQueue();
  }

  get currentPosition(): number {
    return this.processedCount + 1;
  }

  get totalCount(): number {
    return this.processedCount + this.remainingCount;
  }

  /**
   * Advances the queue only if the item is still the one being processed. A manual Skip during the
   * screenshot upload already moved on — skipping again would drop the next item unprocessed.
   */
  private completeIfCurrent(item: PanoramerQueueItem): void {
    if (this.activeItem === item) {
      this.skipCurrent();
    }
  }

  /**
   * True for a late event from the viewer of a previous item: viewerVisible goes false the moment
   * the item changes and true only once the new viewer is shown, so the old viewer can still emit
   * in between and would otherwise be credited to (and skip) the new item.
   */
  private isFromReplacedViewer(): boolean {
    return !this.viewerVisible;
  }

  /**
   * True when the item's upload was stopped during the screenshot upload. The frame is dropped
   * and the queue is not advanced — removeUpload already moved it on to the next item.
   */
  private dropIfAborted(item: PanoramerQueueItem): boolean {
    if (item !== this.abortedItem) return false;
    this.abortedItem = null;
    return true;
  }

  private discardPendingFrames(uploadId: string): void {
    const id = uploadId.toLowerCase();
    for (const [fileUrl, entry] of this.pendingFrames) {
      if (entry.uploadId.toLowerCase() === id) {
        this.pendingFrames.delete(fileUrl);
      }
    }
  }

  private addFrame(item: PanoramerQueueItem, frame: DamageImageFrame): void {
    let entry = this.pendingFrames.get(item.fileUrl);
    if (!entry) {
      entry = { uploadId: item.uploadId, uploadedPathId: item.uploadedPathId, fileId: item.fileId, frames: [] };
      this.pendingFrames.set(item.fileUrl, entry);
    }
    entry.frames.push(frame);
  }

  private flushBatch(fileUrl: string): void {
    const entry = this.pendingFrames.get(fileUrl);
    this.pendingFrames.delete(fileUrl);
    if (!entry || entry.frames.length === 0) return;

    const hasFailures = entry.frames.some(
      (f) => f.status !== DamageImageGenerationStatus.Success
    );

    // Notify upload-detail so it can move this file to the correct column immediately.
    this.panoramerState.notifyBatchReady(fileUrl, hasFailures);

    const model = {
      uploadId: entry.uploadId,
      fileId: entry.fileId,
      connectionId: '',
      damageType: 1,
      damages: entry.frames,
    };

    this.ProcessFilesService.saveIpfFileDamageImages(model).subscribe({
      next: () => {
        console.log(`Batch saved: ${fileUrl} (${entry!.frames.length} frames)`);
        // Set final status via hub (persists to DB + broadcasts to all clients)
        const finalStatus = hasFailures
          ? DamageImageGenerationStatus.SourceFileCorrupted
          : DamageImageGenerationStatus.Success;
        this.ipfHubService.updateFileStatus(entry!.uploadId, entry!.fileId, finalStatus)
          .catch(err => console.error('Failed to update status via hub:', err));
      },
      error: (err) => {
        console.error('Failed to save batch for', fileUrl, err);
      },
    });
  }

  private flushAllBatches(): void {
    for (const fileUrl of Array.from(this.pendingFrames.keys())) {
      this.flushBatch(fileUrl);
    }
  }
}
